import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Card,
  CardContent,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import EOENotReadyTab from '../../components/billing/tabs/EOENotReadyTab';
import EOEPaidTab from '../../components/billing/tabs/EOEPaidTab';
import EOEPendingApprovalTab from '../../components/billing/tabs/EOEPendingApprovalTab';
import EOEPendingPaymentTab from '../../components/billing/tabs/EOEPendingPaymentTab';
import EOEReadyTab from '../../components/billing/tabs/EOEReadyTab';
import EOEReadyToSendTab from '../../components/billing/tabs/EOEReadyToSendTab';
import EOERejectedCancelledTab from '../../components/billing/tabs/EOERejectedCancelledTab';
import { client, getBillingCyclesMeta } from '../../services/api';
import type { BillingCycleTabCounts } from '../../services/api/types.gen';
import { useStore } from '../../store';

const CLAIM_TABS = [
  { id: 'not-ready', label: 'Not Ready', countKey: 'not_ready' },
  { id: 'ready', label: 'Ready', countKey: 'ready' },
  {
    id: 'pending-approval',
    label: 'Pending Approval',
    countKey: 'pending_approval',
  },
  { id: 'ready-to-send', label: 'Ready to Send', countKey: 'ready_to_send' },
  {
    id: 'pending-payment',
    label: 'Pending Payment',
    countKey: 'pending_payment',
  },
  { id: 'paid', label: 'Paid', countKey: 'paid' },
  {
    id: 'rejected-cancelled',
    label: 'Rejected / Cancelled',
    countKey: 'rejected_cancelled',
  },
] as const;

type TabId = (typeof CLAIM_TABS)[number]['id'];

const EMPTY_TAB_COUNTS: Required<BillingCycleTabCounts> = {
  not_ready: 0,
  ready: 0,
  pending_approval: 0,
  ready_to_send: 0,
  pending_payment: 0,
  paid: 0,
  rejected_cancelled: 0,
};

interface EOEStatusTabProps {
  onCountsInvalidated?: () => Promise<void> | void;
}

const TAB_COMPONENTS: Record<TabId, React.ComponentType<EOEStatusTabProps>> = {
  'not-ready': EOENotReadyTab,
  ready: EOEReadyTab,
  'pending-approval': EOEPendingApprovalTab,
  'ready-to-send': EOEReadyToSendTab,
  'pending-payment': EOEPendingPaymentTab,
  paid: EOEPaidTab,
  'rejected-cancelled': EOERejectedCancelledTab,
};

const getCountText = (
  tabCounts: BillingCycleTabCounts | null,
  countKey: keyof BillingCycleTabCounts
) => {
  const count = tabCounts?.[countKey];
  return typeof count === 'number' ? count.toString() : '-';
};

const TabCountPill = ({ label, value }: { label: string; value: string }) => (
  <span
    aria-label={
      value === '-' ? `${label} count unavailable` : `${label} count ${value}`
    }
    className='inline-flex h-4 min-w-5 shrink-0 items-center justify-center rounded-full bg-background/80 px-1.5 text-[10px] font-semibold leading-none text-muted-foreground tabular-nums ring-1 ring-border/60 group-data-[state=active]:bg-white/20 group-data-[state=active]:text-white group-data-[state=active]:ring-white/25'
  >
    {value}
  </span>
);

const EOEClaimsManager: React.FC = () => {
  const showError = useStore(state => state.showError);
  const defaultTabId = CLAIM_TABS[0].id;
  const [activeTab, setActiveTab] = useState<TabId>(defaultTabId);
  const [mountedTabIds, setMountedTabIds] = useState<Set<TabId>>(
    () => new Set([defaultTabId])
  );
  const [tabCounts, setTabCounts] = useState<BillingCycleTabCounts | null>(
    null
  );
  const metadataRequestIdRef = useRef(0);

  const loadMetadata = useCallback(async () => {
    const requestId = metadataRequestIdRef.current + 1;
    metadataRequestIdRef.current = requestId;
    try {
      const response = await getBillingCyclesMeta({ client });
      if (metadataRequestIdRef.current !== requestId) {
        return;
      }
      if (response.error) {
        throw response.error;
      }
      setTabCounts({
        ...EMPTY_TAB_COUNTS,
        ...response.data?.tab_counts,
      });
    } catch (error) {
      if (metadataRequestIdRef.current !== requestId) {
        return;
      }
      console.error('Failed to load billing cycle metadata', error);
      showError('Failed to load billing cycle metadata');
      setTabCounts(null);
    }
  }, [showError]);

  useEffect(() => {
    void loadMetadata();
  }, [loadMetadata]);

  const handleTabChange = useCallback((value: string) => {
    const id = value as TabId;
    setActiveTab(id);
    setMountedTabIds(prev => new Set(prev).add(id));
  }, []);

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl font-bold'>Episodic Claims</h1>
        <p className='text-muted-foreground text-sm'>
          Manage and track episodic claims through the billing lifecycle
        </p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className='flex w-full flex-col gap-6'
        variant='purple'
      >
        <TabsList className='grid h-auto min-h-8 w-full grid-cols-1 gap-0.5 rounded-md border border-border bg-muted/40 p-1 min-[520px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7'>
          {CLAIM_TABS.map(tab => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className='group min-h-8 whitespace-normal px-1 py-1.5 text-[11px] font-medium leading-tight [overflow-wrap:anywhere] data-[state=active]:shadow-none min-[520px]:px-2 sm:text-xs lg:text-[13px] xl:text-sm'
            >
              <span className='inline-flex min-w-0 max-w-full flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-center [overflow-wrap:anywhere] sm:flex-nowrap'>
                <span className='min-w-0 [overflow-wrap:anywhere] sm:whitespace-nowrap'>
                  {tab.label}
                </span>
                <TabCountPill
                  label={tab.label}
                  value={getCountText(tabCounts, tab.countKey)}
                />
              </span>
            </TabsTrigger>
          ))}
        </TabsList>

        <Card>
          <CardContent className='py-6'>
            {CLAIM_TABS.map(tab => {
              const TabComponent = TAB_COMPONENTS[tab.id];
              return (
                <TabsContent key={tab.id} value={tab.id} className='m-0'>
                  {mountedTabIds.has(tab.id) ? (
                    <TabComponent onCountsInvalidated={loadMetadata} />
                  ) : null}
                </TabsContent>
              );
            })}
          </CardContent>
        </Card>
      </Tabs>
    </div>
  );
};

export default EOEClaimsManager;
